import { useEffect } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { usePageMeta } from '@/hooks/usePageMeta';
import { Heart, Globe, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ABOUT_DESCRIPTION =
  "Learn about Devotion Fund—our story, mission, and values. We're building a platform to help Christian workers worldwide raise support and connect with their communities.";

export default function About() {
    usePageMeta('About', ABOUT_DESCRIPTION);
    const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation();
    const { ref: storyRef, isVisible: storyVisible } = useScrollAnimation();
    const { ref: valuesRef, isVisible: valuesVisible } = useScrollAnimation();
    const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation();
    const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const faqs = [
        { question: t('aboutPage.faq.q1.question'), answer: t('aboutPage.faq.q1.answer') },
        { question: t('aboutPage.faq.q2.question'), answer: t('aboutPage.faq.q2.answer') },
        { question: t('aboutPage.faq.q3.question'), answer: t('aboutPage.faq.q3.answer') },
        { question: t('aboutPage.faq.q4.question'), answer: t('aboutPage.faq.q4.answer') },
        { question: t('aboutPage.faq.q5.question'), answer: t('aboutPage.faq.q5.answer') },
        { question: t('aboutPage.faq.q6.question'), answer: t('aboutPage.faq.q6.answer') },
    ];

    useEffect(() => {
        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
        };
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-about-faq-jsonld', 'true');
        el.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, [faqs]);

    return (
        <div className="min-h-screen bg-background">
            <HomeHeader />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 bg-background">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-12">
                        <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-4">
                            {t('app.name')}
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight tracking-tight">
                            {t('aboutPage.hero.title')}
                        </h1>
                        <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                            {t('aboutPage.hero.subtitle')}
                        </p>
                    </div>
                    <div className="aspect-[16/7] w-full overflow-hidden rounded-[2rem] shadow-xl">
                        <img
                            src="/images/joy-children.jpg"
                            alt="Children laughing together"
                            className="w-full h-full object-cover"
                            loading="eager"
                            {...{ fetchpriority: 'high' }}
                        />
                    </div>
                </div>
            </section>

            {/* Our Story Section */}
            <section
                ref={storyRef}
                className={`py-20 px-6 transition-all duration-1000 ${storyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-5">
                            <div className="aspect-[4/5] w-full overflow-hidden rounded-[2rem] shadow-lg sticky top-32">
                                <img
                                    src="/images/classroom-nigeria.jpg"
                                    alt="Teacher and students in a classroom"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 leading-tight">
                                {t('aboutPage.ourStory.title')}
                            </h2>
                            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                                <p>
                                    {t('aboutPage.ourStory.paragraph1')}
                                </p>
                                <p>
                                    {t('aboutPage.ourStory.paragraph2')}
                                </p>
                                <p>
                                    {t('aboutPage.ourStory.paragraph3')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission Section */}
            <section
                ref={missionRef}
                className={`py-20 px-6 bg-gradient-to-br from-secondary/10 to-transparent transition-all duration-1000 ${missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
                        {t('aboutPage.ourMission.title')}
                    </h2>
                    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-border">
                        <p className="text-xl text-foreground leading-relaxed mb-6">
                            {t('aboutPage.ourMission.paragraph1')}
                        </p>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            {t('aboutPage.ourMission.paragraph2')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section
                ref={valuesRef}
                className={`py-20 px-6 transition-all duration-1000 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                        {t('aboutPage.values.title')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className={`bg-white rounded-2xl p-8 shadow-sm border border-border transition-all duration-1000 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                            }`} style={{ transitionDelay: '200ms' }}>
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                                <Heart className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3 text-foreground">{t('aboutPage.values.faith.title')}</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                {t('aboutPage.values.faith.description')}
                            </p>
                        </div>

                        <div className={`bg-white rounded-2xl p-8 shadow-sm border border-border transition-all duration-1000 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                            }`} style={{ transitionDelay: '400ms' }}>
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                                <Globe className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3 text-foreground">{t('aboutPage.values.globalCommunity.title')}</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                {t('aboutPage.values.globalCommunity.description')}
                            </p>
                        </div>

                        <div className={`bg-white rounded-2xl p-8 shadow-sm border border-border transition-all duration-1000 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                            }`} style={{ transitionDelay: '600ms' }}>
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                                <Users className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3 text-foreground">{t('aboutPage.values.authenticConnection.title')}</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                {t('aboutPage.values.authenticConnection.description')}
                            </p>
                        </div>

                        <div className={`bg-white rounded-2xl p-8 shadow-sm border border-border transition-all duration-1000 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                            }`} style={{ transitionDelay: '800ms' }}>
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                                <Sparkles className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3 text-foreground">{t('aboutPage.values.simplicityExcellence.title')}</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                {t('aboutPage.values.simplicityExcellence.description')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Vision Section */}
            <section className="py-20 px-6 bg-gradient-to-br from-primary/5 to-secondary/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                        {t('aboutPage.vision.title')}
                    </h2>
                    <p className="text-xl text-muted-foreground leading-relaxed mb-8">
                        {t('aboutPage.vision.paragraph1')}
                    </p>
                    <p className="text-xl text-foreground font-medium">
                        {t('aboutPage.vision.paragraph2')}
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section
                ref={faqRef}
                className={`py-20 px-6 bg-gradient-to-br from-primary/5 to-secondary/10 transition-all duration-1000 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            {t('aboutPage.faq.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            {t('aboutPage.faq.subtitle')}
                        </p>
                    </div>
                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 shadow-sm border border-border"
                            >
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {faq.question}
                                </h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    {faq.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section
                ref={ctaRef}
                className={`py-24 bg-primary transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        {t('aboutPage.cta.title')}
                    </h2>
                    <p className="text-xl text-white/90 mb-10 leading-relaxed">
                        {t('aboutPage.cta.subtitle')}
                    </p>
                    <div className="flex justify-center">
                        <Button
                            size="lg"
                            className="bg-white hover:bg-white/90 text-primary text-lg px-8 py-6 rounded-full font-semibold"
                            onClick={() => navigate('/signup-creator')}
                        >
                            {t('aboutPage.cta.button1')}
                        </Button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
